import FilterRacesView from '@utmb/types/view/FilterRacesView';
import SearchQualifiersRaceView from '@utmb/types/view/SearchQualifiersRaceView';
import { StoredFilters } from '@utmb/types/qualifiers';

export interface Action {
    type: string;
}

export interface State {
    isLoaded: boolean;
    isMoreLoaded: boolean;
    races: SearchQualifiersRaceView[];
    filters: FilterRacesView;
    search: string;
    selectedCategories: string[];
    selectedCountries: string[];
    selectedDateMin: Date;
    selectedDateMax: Date;
    minDate: Date;
    maxDate: Date;
    nbHits: number;
    offset: number;
    limit: number;
}

export enum ActionKind {
    CHANGE_PAGE_QUALIFIERS = 'CHANGE_PAGE_QUALIFIERS',
    ADD_NEW_QUALIFIERS = 'ADD_NEW_QUALIFIERS',
    SET_SEARCH = 'SET_SEARCH',
    FETCH_QUALIFIERS = 'FETCH_QUALIFIERS',
    SET_QUALIFIERS = 'SET_QUALIFIERS',
    FILTER_ON_CATEGORY = 'FILTER_ON_CATEGORY',
    FILTER_ON_COUNTRY = 'FILTER_ON_COUNTRY',
    FILTER_ON_DATE = 'FILTER_ON_DATE',
    RESTORE_FILTERS = 'RESTORE_FILTERS',
}

export interface AddNewQualifiersAction extends Action {
    payload: {
        races: SearchQualifiersRaceView[];
        offset: number;
    };
}

export type ChangePageQualifiersAction = Action;

export interface SetSearchAction extends Action {
    payload: string;
}

export type FetchQualifiersAction = Action;

export interface SetQualifiersAction extends Action {
    payload: {
        nbHits: number;
        races: SearchQualifiersRaceView[];
        limit: number;
    };
}

export interface FilterOnCategoryAction extends Action {
    payload: string;
}

export interface FilterOnCountryAction extends Action {
    payload: string;
}

export interface FilterOnDateAction extends Action {
    payload: {
        dateMin?: Date;
        dateMax?: Date;
    };
}

export interface RestoreFiltersAction extends Action {
    payload: StoredFilters;
}

const currentYear = new Date().getFullYear();
const minDate: Date = new Date(`2000-01-01`);
const maxDate: Date = new Date(`${currentYear + 1}-12-31`);

export const initialState: State = {
    isLoaded: true,
    isMoreLoaded: true,
    races: [],
    search: '',
    filters: {
        categories: [],
        countries: [],
    },
    selectedCategories: [],
    selectedCountries: [],
    selectedDateMin: new Date(),
    selectedDateMax: maxDate,
    minDate,
    maxDate,
    limit: 0,
    nbHits: 0,
    offset: 0,
};

function isChangePageQualifiersAction(action: Action): action is ChangePageQualifiersAction {
    return action.type === ActionKind.CHANGE_PAGE_QUALIFIERS;
}

function isAddNewQualifiersAction(action: Action): action is AddNewQualifiersAction {
    return action.type === ActionKind.ADD_NEW_QUALIFIERS;
}

function isSetSearchAction(action: Action): action is SetSearchAction {
    return action.type === ActionKind.SET_SEARCH;
}

function isFetchQualifiersAction(action: Action): action is FetchQualifiersAction {
    return action.type === ActionKind.FETCH_QUALIFIERS;
}

function isSetQualifiersAction(action: Action): action is SetQualifiersAction {
    return action.type === ActionKind.SET_QUALIFIERS;
}

function isFilterOnCategoryAction(action: Action): action is FilterOnCategoryAction {
    return action.type === ActionKind.FILTER_ON_CATEGORY;
}

function isFilterOnCountryAction(action: Action): action is FilterOnCountryAction {
    return action.type === ActionKind.FILTER_ON_COUNTRY;
}

function isFilterOnDateAction(action: Action): action is FilterOnDateAction {
    return action.type === ActionKind.FILTER_ON_DATE;
}

function isRestoreFilter(action: Action): action is RestoreFiltersAction {
    return action.type === ActionKind.RESTORE_FILTERS;
}

export function reducer(state: State, action: Action) {
    const update: Partial<State> = {};

    if (isChangePageQualifiersAction(action)) {
        update.isLoaded = false;
    }

    if (isAddNewQualifiersAction(action)) {
        const { races, offset } = action.payload;
        update.races = races;
        update.offset = offset;
        update.isLoaded = true;
    }

    if (isSetSearchAction(action)) {
        update.search = action.payload;
    }

    if (isFetchQualifiersAction(action)) {
        update.isLoaded = false;
    }

    if (isSetQualifiersAction(action)) {
        const { races, nbHits, limit } = action.payload;
        update.races = races;
        update.nbHits = nbHits;
        update.offset = 0;
        update.limit = limit;
        update.isLoaded = true;
    }

    if (isFilterOnCategoryAction(action)) {
        const theme = action.payload;
        update.selectedCategories = state.selectedCategories.includes(theme)
            ? state.selectedCategories.filter((t) => t !== theme)
            : [...state.selectedCategories, theme];
    }

    if (isFilterOnCountryAction(action)) {
        const theme = action.payload;
        update.selectedCountries = state.selectedCountries.includes(theme)
            ? state.selectedCountries.filter((t) => t !== theme)
            : [...state.selectedCountries, theme];
    }

    if (isFilterOnDateAction(action)) {
        update.selectedDateMin = action.payload.dateMin;
        update.selectedDateMax = action.payload.dateMax;
    }

    if (isRestoreFilter(action)) {
        const { search, selectedCategories, selectedCountries, selectedDateMin, selectedDateMax } = action.payload;
        update.search = search;
        update.selectedCategories = selectedCategories;
        update.selectedCountries = selectedCountries;
        update.selectedDateMin = selectedDateMin ?? minDate;
        update.selectedDateMax = selectedDateMax ?? minDate;
    }

    return {
        ...state,
        ...update,
    };
}
